/**
 * useReviewChanges.ts
 *
 * Fetch side of the #969 Part F Review Changes panel: loads each table_graph
 * widget's live column/row labels (or notices it 404s — #969 Part E), and
 * feeds that into the pure diff in utils/reviewChanges.ts.
 *
 * Only active while `config.needs_review` is true — with no lapsed sign-off
 * there is nothing to review, and the per-widget "missing table" / "new
 * columns" fetches this hook drives are also what the dot indicator on
 * DashboardView.tsx keys off (#969 Part F "per-widget change indicators"),
 * so both stay in sync from the same data.
 */

import { useMemo } from 'react'
import axios from 'axios'
import { useQueries } from '@tanstack/react-query'
import { DashboardConfig } from '../types/gotypes'
import { walkTableGraphWidgets } from '../utils/dashboardWidgetWalk'
import { computeReviewChanges, LiveTableInfo, ReviewChangeItem, OrphanedReference } from '../utils/reviewChanges'
import { extractColumnLabels, extractRowLabels } from '../components/platinum-graphs/utils/convertPlatinumData'
import { extractErrorMessage } from '../utils/apitools'
import { isTableNotFoundMessage } from '../utils/tableErrors'
import { API_BASE_URL } from '../constants/system'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { cleanOrphanedReferences } from '../utils/tableViewConfig'

export interface UseReviewChangesResult {
  items: ReviewChangeItem[]
  /** True while any widget's live state is still loading — the panel/dot
   *  indicators should treat "not yet known" as "no change" rather than flash. */
  isLoading: boolean
}

export function useReviewChanges(
  customer: string,
  config: DashboardConfig | undefined,
  orphanedCleared?: OrphanedReference[],
): UseReviewChangesResult {
  const active = Boolean(config?.sign_off && config?.needs_review)
  const refs = useMemo(() => (active && config ? walkTableGraphWidgets(config) : []), [active, config])

  const results = useQueries({
    queries: refs
      .filter(ref => Boolean(ref.data.table_path))
      .map(ref => ({
        queryKey: ['review-changes-table', customer, ref.data.job, ref.data.table_path],
        enabled: Boolean(customer && ref.data.job && ref.data.table_path),
        queryFn: async (): Promise<{ path: string; info: LiveTableInfo }> => {
          try {
            const res = await axios.get<PlatinumData>(`${API_BASE_URL}/tables/${customer}/${ref.data.job}/data`, {
              params: { path: ref.data.table_path, show_freq: true, show_colpct: true },
            })
            // #969 Part B×F wire: the view config's dead references are
            // computed here, where the PlatinumData is in hand, so the panel's
            // "orphaned cleaned" group has a producer without any plumbing
            // from the widget render path.
            const { orphans } = cleanOrphanedReferences(ref.data.view_config, res.data)
            return { path: ref.path, info: { columns: extractColumnLabels(res.data), rows: extractRowLabels(res.data), orphans } }
          } catch (err) {
            if (isTableNotFoundMessage(extractErrorMessage(err))) {
              return { path: ref.path, info: { missing: true } }
            }
            // Transient failure — report nothing for this widget rather than
            // guessing; a network blip must not read as "table missing" or
            // "no changes" (see tableErrors.ts's module doc).
            throw err
          }
        },
        // Transient failures resolve to "unknown" (no item, no crash) —
        // React Query's own retry handles genuine blips; a repeat failure just
        // means this widget stays silently absent from the panel this session.
        retry: 1,
      })),
  })

  const liveTables = useMemo(() => {
    const map: Record<string, LiveTableInfo> = {}
    results.forEach(r => {
      if (r.data) map[r.data.path] = r.data.info
    })
    return map
  }, [results])

  const items = useMemo(() => {
    if (!config) return []
    // Derive orphaned-reference entries from the per-widget clean run above
    // unless the caller supplied its own list. Kind is recovered by membership
    // in the widget's own hide lists; the display label is the path's leaf.
    const derived: OrphanedReference[] = orphanedCleared ? [] : refs.flatMap(ref => {
      const info = liveTables[ref.path]
      if (!info?.orphans?.length) return []
      const hiddenRows = new Set(ref.data.view_config?.hidden_rows || [])
      return info.orphans.map(id => ({
        widgetPath: ref.path,
        pageIndex: ref.pageIndex,
        widgetTitle: ref.data.title || ref.data.table_path.split('/').pop()?.replace(/\.cbt$/, '') || 'Table',
        label: id.split('\u001F').pop() || id,
        kind: (hiddenRows.has(id) ? 'row' : 'column') as OrphanedReference['kind'],
      }))
    })
    return computeReviewChanges({ config, liveTables, orphanedCleared: orphanedCleared ?? derived })
  }, [config, liveTables, orphanedCleared, refs])

  return {
    items,
    isLoading: active && results.some(r => r.isLoading),
  }
}

export default useReviewChanges
